import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { TripResultStatus } from '../Components/TripResultItem';
import { BulkBookingFrequency, BulkBookingWeekOrdinal } from '../Types';

export interface BulkBookingPreviewRequest {
	startDate: Date;
	endDate: Date;
	daysOfWeek: string[];
	/** 'weekly' or 'monthly' — see `Modules/BulkBooking/Types`. */
	frequency: BulkBookingFrequency;
	/** Repeat every N weeks or N months. */
	interval: number;
	/** Monthly patterns only; ignored by the server for weekly ones. */
	weekOrdinals: BulkBookingWeekOrdinal[];
	departureTime: string;
	startLocationId: string;
	endLocationId: string;
}

export interface BulkBookingPreviewItem {
	date: string; // ISO DateTime
	status: TripResultStatus; // 'Scheduled' | 'Closed' | 'SoldOut' | 'NoTripFound' | 'Departed'
	tripId?: string;
	time: string;
	/**
	 * The booking types this trip is closed to, of the ones this operator offers — `Vehicle`,
	 * `Passenger`, `Freight`. Empty when it is open to all of them. `status` is only `Closed` when
	 * every one of them is closed, so this is what says a partly-closed trip is partly closed.
	 */
	closedFor?: string[];
	/**
	 * The vessel running this trip is not offered for walk-on bookings. Separate from `closedFor`
	 * because it is not a closure: the trip's passenger bookings are open, the vessel just isn't
	 * sold for walk-on fares. It reads as its own sentence for that reason.
	 */
	walkOnsNotOfferedOnVessel?: boolean;
}

export interface BulkBookingPreviewResponse {
	/** Empty when `matchedTripCount` exceeds `maxTrips` — the server does not resolve the trips. */
	items: BulkBookingPreviewItem[];
	/** How many trips matched. This, not `items.length`, is what the limit applies to. */
	matchedTripCount: number;
	/** The limit the server enforced. Trust this over the local app config copy. */
	maxTrips: number;
}

const fetchBulkPreview = async (request: BulkBookingPreviewRequest) => {
	const { data } = await axios.post<BulkBookingPreviewResponse>('/api/trips/preview-bulk', request);
	return data;
};

export const usePreviewBulk = (request: BulkBookingPreviewRequest, enabled: boolean) => {
	return useQuery({
		queryKey: ['previewBulk', request],
		queryFn: () => fetchBulkPreview(request),
		enabled,
	});
};
